import React from 'react';

import {createStore, applyMiddleware} from 'redux';
import thunk from 'redux-thunk';
// import logger from 'redux-logger';
import {Provider} from 'react-redux';

import testResultsReducer from '@modules/testResults/reducers';
import TestResultsApp from '@modules/testResults/containers/TestResultsApp.jsx';

import userMaintenanceReducer from '@modules/userMaintenance/reducers';
import UserMaintenanceWnd from '@modules/userMaintenance/containers/UserMaintenanceWnd.jsx';
import {loadTranslations, setLocale, syncTranslationWithStore} from 'react-redux-i18n';
import {userMaintenanceEN} from '@modules/userMaintenance/translations/en.js';
import {userMaintenanceJA} from '@modules/userMaintenance/translations/ja.js';

import {testResultsJA} from '@modules/testResults/translations/ja.js';
import {testResultsEN} from '@modules/testResults/translations/en.js';

import {patientLookupEN} from '@modules/patientLookup/translations/en.js';
import {patientLookupES} from '@modules/patientLookup/translations/es.js';
import {patientLookupJA} from '@modules/patientLookup/translations/ja.js';
import patientLookupReducer from '@modules/patientLookup/reducers';
import PatientLookupApp from '@modules/patientLookup/containers/PatientLookupApp.jsx';

import { setAppConfig } from '@common/utils/config';
import { SpreadSheetsPedigree } from '@modules/spreadsheets/pages/pedigree';
import { SpreadsheetsIndividual } from '@modules/spreadsheets/pages/individual';
import spreadsheetsReducers from '@modules/spreadsheets/reducers';
import { spreadsheetsEN } from '@modules/spreadsheets/translations/en';
import { spreadsheetsJA } from '@modules/spreadsheets/translations/ja';
import { spreadsheetsES } from '@modules/spreadsheets/translations/es';

import { Orders } from '@modules/orders';
import ordersReducers from '@modules/orders/reducers';
import { ordersEN } from '@modules/orders/translations/en';
import { ordersJA } from '@modules/orders/translations/ja';
import { ordersES } from '@modules/orders/translations/es';

import { Letters } from '@modules/letters';
import lettersReducers from '@modules/letters/reducers';
import { lettersEN } from '@modules/letters/translations/en';
import { lettersJA } from '@modules/letters/translations/ja';
import { lettersES } from '@modules/letters/translations/es';

import { initLookupTableApp } from './modules/lookupTable';
import { initProfileSettings } from './modules/profile-settings';
import { initEmailConfirmation } from './modules/userMaintenance/email-confirmation';
import { initAuditReportApp } from './modules/audit-report';
import { initDatabaseSecurity } from './modules/database-security';
import { initPedigreeImport } from './modules/pedigrees/components/pedigree-import';
import { initNewPedigreeWorkflow } from './modules/pedigrees/components/new-pedigree';
import { initIndividuals } from './modules/individuals';
import { initPedigrees } from '@modules/pedigrees';
import { initInviteStatus } from '@modules/invite-status';
import { initEmailTemplates } from '@modules/email-templates';

import { mountReactNode } from '@common/utils/react';
import { initFhqDesignTool } from '@modules/fhq-design-tool';
import {initSpreadsheetDetails} from "@modules/spreadsheets/pages/spreadsheet-details";

import './index.styles.scss';
import {initDatasheetsEditor, initDatasheetsHome, initDatasheetsResult, unmountDatasheetsResult, clearDatasheetTabPreferencesByPrefix} from "@modules/datasheets";

window.reactInitTestResults = function(componentId, patientId, patientName, locale) {
    const patientState = {
        patient: {
            id: patientId,
            name: patientName
        },
        tests: [],
        currentTestId: undefined,
        currentDoc: undefined,
        testDetails: {
            test: {
                id: undefined,
                submittedDate: undefined,
                reportedDate: undefined,
                reclassDate: undefined,
                lab: undefined,
                testOrdered: undefined,
                testStatus: undefined,
                documents: [],
                notes: undefined,
                genes: [],
                potentialFamilyMemberReports: undefined
            },
            show: false,
            saving: false,
            errors: {
                submittedDate: false,
                reportedDate: false,
                reclassDate: false
            },
            files: [],
            toDelete: [],
            multipleReports: {
                show: false
            },
        },
        labs: undefined,
        genes: undefined,
        ambryStatus: undefined,
        messageBox: {
            type: undefined,
            header: '',
            text: '',
            cancelText: undefined,
            confirmText: undefined,
            show: false
        },
        testColumns: [
            {
                title: 'Actions',
                width: 60,
                minWidth: 60,
                default: undefined
            },
            {
                title: 'Lab',
                width: undefined,
                minWidth: 60,
                default: 7
            },
            {
                title: 'Test',
                width: undefined,
                minWidth: 70,
                default: 15
            },
            {
                title: 'Submitted Date',
                width: undefined,
                minWidth: 100,
                default: 7
            },
            {
                title: 'Reported Date',
                width: undefined,
                minWidth: 100,
                default: 7
            },
            {
                title: 'Reclass Date',
                width: undefined,
                minWidth: 100,
                default: 7
            },
            {
                title: 'Status',
                width: undefined,
                minWidth: 70,
                default: 7
            },
            {
                title: 'Result',
                width: undefined,
                minWidth: 70,
                default: 15
            },
            {
                title: 'Document',
                width: undefined,
                minWidth: 70,
                default: 7
            },
            {
                title: 'Notes',
                width: undefined,
                minWidth: 260,
                default: 20
            }
        ]
    };

    const store = createStore(testResultsReducer, patientState, applyMiddleware(thunk));

    syncTranslationWithStore(store);
    const translationsObject = {
        en: testResultsEN,
        ja: testResultsJA
    };
    if (!translationsObject[locale]) {
        locale = "en";
    }

    store.dispatch(loadTranslations(translationsObject));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store} key={patientId}>
            <TestResultsApp id={patientId} name={patientName}/>
        </Provider>,
        document.getElementById(componentId)
    );
};

window.reactInitUserMaintenance = function(componentId, locale, liveSearchEnabled, liveSearchDelay) {
    setAppConfig({
        userMaintenance: {
            liveSearchEnabled,
            liveSearchDelay,
        }
    });

    const userState = {

    };

    const store = createStore(userMaintenanceReducer, userState, applyMiddleware(thunk));
    syncTranslationWithStore(store);
    const translationsObject = {
        en: userMaintenanceEN,
        ja: userMaintenanceJA
    };
    if (!translationsObject[locale]) {
        locale = "en";
    }
    store.dispatch(loadTranslations(translationsObject));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store}>
            <UserMaintenanceWnd />
        </Provider>,
        document.getElementById(componentId)
    );
};

window.reactInitPatientLookup = function(componentId, questionnaireId, title, mrnField, dobField, createNewRecords, locale, version, baseUrl) {
    const patientLookupState = {
        questionnaire: {
            id: questionnaireId,
            title: title,
            mrnField: mrnField,
            dobField: dobField,
            createNewRecords: createNewRecords
        },
        patients: {
            searchValue: undefined,
            enteredDOB: undefined,
            enteredDOBValid: true,
            selected: undefined,
            patientsInfo: []
        },
        progenyInfo: {
            version: version,
            baseUrl: baseUrl
        }
    };

    const store = createStore(patientLookupReducer, patientLookupState, applyMiddleware(thunk));

    syncTranslationWithStore(store);
    const translationsObject = {
        en: patientLookupEN,
        ja: patientLookupJA,
        es: patientLookupES
    };
    if (!translationsObject[locale]) {
        locale = "en";
    }

    store.dispatch(loadTranslations(translationsObject));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store} key={questionnaireId}>
            <PatientLookupApp />
        </Provider>,
        document.getElementById(componentId)
    );
};

window.initIndividualSpreadsheets = function(componentId, locale, liveSearchEnabled, liveSearchDelay) {
    setAppConfig({
        individualSpreadsheet: {
            liveSearchEnabled,
            liveSearchDelay,
        }
    });

    const state = {};
    const store = createStore(spreadsheetsReducers, state, applyMiddleware(thunk));

    syncTranslationWithStore(store);

    const translation = {
        en: spreadsheetsEN,
        ja: spreadsheetsJA,
        es: spreadsheetsES
    };

    if (!translation[locale]) {
        locale = 'en';
    }

    store.dispatch(loadTranslations(translation));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store}>
            <SpreadsheetsIndividual />
        </Provider>,
        document.getElementById(componentId)
    );
};

window.initPedigreeSpreadsheets = function(componentId, locale, liveSearchEnabled, liveSearchDelay) {
    setAppConfig({
        pedigreeSpreadsheet: {
            liveSearchEnabled,
            liveSearchDelay,
        }
    });

    const state = {};
    const store = createStore(spreadsheetsReducers, state, applyMiddleware(thunk));

    syncTranslationWithStore(store);

    const translation = {
        en: spreadsheetsEN,
        ja: spreadsheetsJA,
        es: spreadsheetsES
    };

    if (!translation[locale]) {
        locale = 'en';
    }

    store.dispatch(loadTranslations(translation));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store}>
            <SpreadSheetsPedigree />
        </Provider>,
        document.getElementById(componentId)
    );
};

window.initOrders = function(componentId, locale, liveSearchEnabled, liveSearchDelay) {
    setAppConfig({
        orders: {
            liveSearchEnabled,
            liveSearchDelay,
        }
    });

    const state = {};
    const store = createStore(ordersReducers, state, applyMiddleware(thunk));

    syncTranslationWithStore(store);

    const translation = {
        en: ordersEN,
        ja: ordersJA,
        es: ordersES
    };

    if (!translation[locale]) {
        locale = 'en';
    }

    store.dispatch(loadTranslations(translation));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store}>
            <Orders />
        </Provider>,
        document.getElementById(componentId)
    );
};

window.initLetters = function(componentId, locale, liveSearchEnabled, liveSearchDelay) {
    setAppConfig({
        letters: {
            liveSearchEnabled,
            liveSearchDelay,
        }
    });

    const state = {};
    const store = createStore(lettersReducers, state, applyMiddleware(thunk));

    syncTranslationWithStore(store);

    const translation = {
        en: lettersEN,
        ja: lettersJA,
        es: lettersES
    };

    if (!translation[locale]) {
        locale = 'en';
    }

    store.dispatch(loadTranslations(translation));
    store.dispatch(setLocale(locale));

    mountReactNode(
        <Provider store={store}>
            <Letters />
        </Provider>,
        document.getElementById(componentId)
    );
};

window.initProfileSettings = initProfileSettings;

window.initEmailConfirmation = initEmailConfirmation;

window.initLookupTable = initLookupTableApp;

window.initFieldAuditReport = initAuditReportApp;

window.initDatabaseSecurity = initDatabaseSecurity;

window.initPedigreeImport = initPedigreeImport;

window.initNewPedigreeWorkflow = initNewPedigreeWorkflow;

window.initIndividuals = initIndividuals;

window.initFhqDesignTool = initFhqDesignTool;

window.initPedigrees = initPedigrees;

window.initInviteStatus = initInviteStatus;

window.initEmailTemplates = initEmailTemplates;

window.initSpreadsheetDetails = initSpreadsheetDetails;

window.initDatasheetsHome = initDatasheetsHome;

window.initDatasheetsEditor = initDatasheetsEditor;

window.initDatasheetsResult = initDatasheetsResult;

window.unmountDatasheetsResult = unmountDatasheetsResult;

window.clearDatasheetTabPreferencesByPrefix = clearDatasheetTabPreferencesByPrefix;

